import React, { useState } from 'react';
import { KibSheet } from '@chewy/kib-overlays-react';
import '@chewy/kib-overlays-styles/dist/styles/kib-sheet.css';
import { KibButtonNew } from '@chewy/kib-controls-react';
import { KibInputText } from '@chewy/kib-fields-react';
import { KibAlert } from '@chewy/kib-messaging-react';
import {
  CLINIC_NAME_MAX_ENTRY_LENGTH,
  CLINIC_NAME_MINIMUM,
  MOBILE_BREAKPOINT,
} from '../../../api/constant';
import { usePetForm } from '../PetFormContext/PetFormContext';
import {
  createVetClinicApi,
  findClinicsByPhone,
  handleVetClinicError,
} from '../../../api/vetClinicApi';
import { isValidPhone } from '../../../util/isValidPhone';
import { isValidZIPCode } from '../../../util/isValidZIPCode';
import { scrollBody } from '../../../util/scrollBody';
import { scrollDocumentToVet } from '../util/scrollDocumentToVet';
import { isValidPhoneCharacter } from '../../../util/isValidPhoneCharacter';
import { isValidClinicInput } from './vetUtils/isValidClinicInput';
import { Clinic, Clinics } from './vetClinicResponse';
import {
  onboardingVetName,
  onboardingVetPhone,
  onboardingVetText,
  searchVetText,
  zipCodeText,
} from './vetContent';
import VetOnboardingButtons from './VetOnboardingButtons';
import AddVetSheet from './AddVetSheet';

const OnboardingVetClinic = ({
  clinics,
  setClinics,
}: {
  clinics: Clinics;
  setClinics: React.Dispatch<React.SetStateAction<Clinics>>;
}) => {
  const {
    showAddVetSheet,
    setShowAddVetSheet,
    showOnboardingVetSheet,
    setShowOnboardingVetSheet,
    setTriggerSearchOnload,
    setPrePopulatedSearchResults,
    setClinicSearchValue,
    setNewClinic,
    setDisplayCreateErrorSnackbar,
  } = usePetForm();
  const [vetNameValue, setVetNameValue] = useState('');
  const [vetPhoneValue, setVetPhoneValue] = useState('');
  const [vetZIPValue, setVetZIPValue] = useState('');
  const [matchingClinic, setMatchingClinic] = useState<boolean>(false);
  const [vetNameErrors, setVetNameErrors] = useState<Array<string>>([]);
  const [vetPhoneErrors, setVetPhoneErrors] = useState<Array<string>>([]);
  const [vetZIPErrors, setVetZIPErrors] = useState<Array<string>>([]);
  const [showValidations, setShowValidations] = useState<boolean>(false);
  const [addingClinicLoading, setAddingClinicLoading] = useState(false);
  const [showMatchingErrors, setShowMatchingErrors] = useState<boolean>(false);
  const [matchingClinics, setMatchingClincs] = useState<Array<Clinic>>([]);

  const messageVetPhoneExists = 'This phone number is already linked to another clinic.';
  const titleMatchingClinic = 'We found a matching vet clinic.';
  const messageFoundMatchingClinic =
    'A vet clinic in our system matches the information you gave us.';
  const messageViewMatchingClinic = 'View Matching Clinic';
  const messageNameisRequired = 'Clinic name is required.';
  const messageNameLengthInvalid = 'Clinic name must be at least 3 characters.';
  const messagePhoneisRequired = 'Phone number is required.';
  const messageZIPInvalid = 'Enter a valid ZIP code.';
  const messageInvalidPhone = 'Enter a valid phone number.';

  const resetErrors = () => {
    vetNameErrors.length > 0 && setVetNameErrors([]);
    vetPhoneErrors.length > 0 && setVetPhoneErrors([]);
    vetZIPErrors.length > 0 && setVetZIPErrors([]);
    showValidations && setShowValidations(false);
  };

  const handleCancelOnboardingVetSheet = () => {
    setShowOnboardingVetSheet(false);
    setVetNameValue('');
    setVetPhoneValue('');
    setVetZIPValue('');
    matchingClinic && setMatchingClinic(false);
    resetErrors();
    scrollBody(true);
    scrollDocumentToVet();
  };

  const handleViewMatchingClinic = () => {
    setPrePopulatedSearchResults(matchingClinics);
    setTriggerSearchOnload(true);
    setShowOnboardingVetSheet(false);
    setShowAddVetSheet(true);
    scrollBody(false);
  };

  const setFormErrorsAndCheckValidity = (): boolean => {
    let hasErrors = false;
    setShowValidations(true);
    if (vetNameValue.length === 0) {
      setVetNameErrors([messageNameisRequired]);
      hasErrors = true;
    } else if (vetNameValue.trim().length < CLINIC_NAME_MINIMUM) {
      setVetNameErrors([messageNameLengthInvalid]);
      hasErrors = true;
    }
    if (vetPhoneValue.length === 0) {
      setVetPhoneErrors([messagePhoneisRequired]);
      hasErrors = true;
    } else if (!isValidPhone(vetPhoneValue)) {
      setVetPhoneErrors([messageInvalidPhone]);
      hasErrors = true;
    }
    if (vetZIPValue.length > 0 && !isValidZIPCode(vetZIPValue)) {
      setVetZIPErrors([messageZIPInvalid]);
      hasErrors = true;
    }

    return hasErrors;
  };

  const handleClickAdd = () => {
    resetErrors();
    setShowValidations(true);
    if (!!setFormErrorsAndCheckValidity()) {
      return false;
    } else {
      handleAddNewVet();
    }
  };

  const handleAddNewVet = async () => {
    async function createClinic() {
      try {
        setAddingClinicLoading(true);
        const response = await createVetClinicApi.create(vetNameValue, vetPhoneValue, vetZIPValue);
        const newClinic: Clinic = {
          id: Number(JSON.stringify(response)),
          name: vetNameValue,
          address: {
            phone1: vetPhoneValue,
            postcode: vetZIPValue,
          },
        };
        if (!!response) {
          setNewClinic(newClinic);
        }
      } catch (error) {
        setDisplayCreateErrorSnackbar(true);
        handleVetClinicError(error, 'addNewClinic');
        // Error handling to be added with https://chewyinc.atlassian.net/browse/PPF-974
      } finally {
        setAddingClinicLoading(false);
        setShowOnboardingVetSheet(false);
        scrollBody(true);
        scrollDocumentToVet();
      }
    }
    const matchingClinics: Clinics = await findClinicsByPhone.get(vetPhoneValue);
    if (matchingClinics && matchingClinics.length > 0) {
      setMatchingClinic(true);
      setMatchingClincs(matchingClinics);
      setClinicSearchValue(vetPhoneValue);
      setVetPhoneErrors([messageVetPhoneExists]);
      setShowMatchingErrors(true);
      return;
    } else {
      createClinic();
    }
  };

  return (
    <>
      <KibSheet
        open={showOnboardingVetSheet}
        onClose={handleCancelOnboardingVetSheet}
        title={searchVetText}
        size="large"
        data-testid="onboarding-clinic-sheet"
      >
        <div className="pet-form-vet-onboarding__sheet" data-testid="pet-form-vet-onboarding-sheet">
          <form id="vet-clinic-onboarding">
            <div className="kib-margin__bottom">{onboardingVetText}</div>
            <KibInputText
              data-testid="pet-form-vet-onboarding-name"
              label={onboardingVetName}
              value={vetNameValue}
              required={true}
              onChange={(newValue) => {
                if (
                  isValidClinicInput(newValue.target.value) &&
                  newValue.target.value.length <= CLINIC_NAME_MAX_ENTRY_LENGTH
                ) {
                  setVetNameValue(newValue.target.value);
                }
              }}
              invalid={showValidations && vetNameErrors.length > 0}
              messagesContent={showValidations && vetNameErrors}
            />
            <span className="pet-form-vet-onboarding__details">
              <KibInputText
                data-testid="pet-form-vet-onboarding-phone-number"
                className="pet-form-vet-onboarding__number"
                label={onboardingVetPhone}
                value={vetPhoneValue}
                required={true}
                onChange={(newValue) => {
                  if (isValidPhoneCharacter(newValue.target.value)) {
                    setVetPhoneValue(newValue.target.value);
                  }
                }}
                invalid={(showValidations || showMatchingErrors) && vetPhoneErrors.length > 0}
                messagesContent={showValidations && vetPhoneErrors}
              />
              <KibInputText
                data-testid="pet-form-vet-onboarding-zip"
                className="pet-form-vet-onboarding__ZIP"
                label={zipCodeText}
                value={vetZIPValue}
                onChange={(newValue) => setVetZIPValue(newValue.target.value)}
                invalid={showValidations && vetZIPErrors.length > 0}
                messagesContent={showValidations && vetZIPErrors}
              />
            </span>
          </form>
          {matchingClinic && (
            <div className="pet-form-vet-onboarding__alert">
              <KibAlert title={titleMatchingClinic}>{messageFoundMatchingClinic}</KibAlert>
              <KibButtonNew
                className="pet-form-vet-onboarding__matching-view"
                data-testid="pet-form-vet-onboarding-matching-view"
                emphasis="secondary"
                size="large"
                onClick={handleViewMatchingClinic}
              >
                {messageViewMatchingClinic}
              </KibButtonNew>
            </div>
          )}
          {showOnboardingVetSheet && (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {window.outerWidth <= MOBILE_BREAKPOINT ? (
                <VetOnboardingButtons
                  mobileView={true}
                  handleCancelOnboardingVetSheet={handleCancelOnboardingVetSheet}
                  handleClickAdd={handleClickAdd}
                  disable={addingClinicLoading}
                />
              ) : (
                <VetOnboardingButtons
                  mobileView={false}
                  handleCancelOnboardingVetSheet={handleCancelOnboardingVetSheet}
                  handleClickAdd={handleClickAdd}
                  disable={addingClinicLoading}
                />
              )}
            </>
          )}
        </div>
      </KibSheet>
      {showAddVetSheet && <AddVetSheet clinics={clinics} setClinics={setClinics} />}
    </>
  );
};

export default OnboardingVetClinic;
